import React, { Component } from 'react'
import { withRouter } from 'react-router-dom'
import { injectIntl } from 'react-intl'

import { Layout, Container, Content, PageTitle } from 'src/components/Layout'
import SvgIcon from 'src/components/SvgIcon'
import { Loader } from 'src/components/Loader'
import { getPlain } from 'src/utils/request-client'
import { isIos, isEmpty, logError } from 'src/utils/helpers'
import { openAddToWalletUrl, getAddToWalletUrl } from 'src/modules/Documents/helper'
import injectLanguage from 'src/components/LanguageWrapper/inject'
import FontAwesomeIconComponent from 'src/components/FontAwesomeIcon/index'
import Button from 'src/components/Button/Button'
import SpeedDial from 'src/components/Menu/SpeedDial'
import { WidgetContext } from 'src/modules/App/WidgetContext'

import styles from './styles.module.less'
import intlMessages from '../translations'
import LockModal from './LockModal'

const EXCLUDED_FOR_BROKERS = ['myinsurancebroker', 'assurancetwis']

class DocumentView extends Component {
  state = {
    url: null,
    hasPreview: false,
    loading: false,
    fetchingDownload: false,
    showLockModal: false,
  }

  componentDidMount() {
    const { document } = this.props

    if (!document) {
      this.props.history.push('/documents')
      return
    }

    const url = this.getLinkUrl('documents.preview')
    if (!url) {
      return
    }
    this.setState({ loading: true })
    getPlain(url)
      .then(({ url, hasPreview }) => {
        this.setState({
          loading: false,
          url,
          hasPreview,
        })
      })
      .catch(() => {
        this.setState({
          loading: false,
        })
      })
  }

  getLinkUrl = type => {
    const defaultResponse = null

    try {
      const { document } = this.props

      if (!(document && document.hasOwnProperty('links') && !isEmpty(document.links))) {
        return defaultResponse
      }

      const link = document.links.find(link => link.rel === type)

      if (!(link && link.hasOwnProperty('url'))) {
        return defaultResponse
      }

      return link.url
    } catch (e) {
      return defaultResponse
    }
  }

  openNewTab = url => {
    try {
      if (!url) {
        return false
      }

      const win = window.open(url, '_blank')
      if (win && win.hasOwnProperty('focus')) {
        win.focus()
      }
    } catch (e) {
      logError('Could not open link in new tab', e)
      return false
    }
  }

  downloadDocument = downloadUrl => {
    const { document } = this.props

    if (document.type === 'pinkSlips' || document.type === 'fleetSlips') {
      this.openNewTab(downloadUrl)
    } else {
      this.setState({ fetchingDownload: true })
      if (isIos()) {
        let windowOpenReference = window.open()
        getPlain(downloadUrl).then(({ url }) => {
          this.setState({ fetchingDownload: false })
          windowOpenReference.location = url
        })
      } else {
        getPlain(downloadUrl).then(({ url }) => {
          this.setState({ fetchingDownload: false })
          this.openNewTab(url)
        })
      }
    }
  }

  addToWallet = (walletUrl, documentId) => {
    const { setAddedToWallet } = this.props

    // Mark this pink slip as added, so that we can hide button from summary view
    if (typeof setAddedToWallet === 'function') {
      setAddedToWallet(documentId)
    }

    openAddToWalletUrl(walletUrl)
  }

  redirect = link => {
    const { history } = this.props

    history.push(link)
  }

  handleBack = () => {
    const { history } = this.props
    history.push('/documents')
  }

  handleLockModal = () => {
    this.setState(prevState => {
      return {
        showLockModal: !prevState.showLockModal,
      }
    })
  }

  handleDownloadDocument = () => {
    // Add Download action
    const downloadUrl = this.getLinkUrl('documents.download')
    if (downloadUrl) {
      this.downloadDocument(downloadUrl)
    } else {
      return
    }
  }
  /**
   * Check if we need to show "Add to Wallet" button for provided slip/document.
   *
   * @param {object} slip
   * @return {boolean}
   */
  isWalletButtonVisible = slip => {
    const { addedToWallet } = this.props

    if (!getAddToWalletUrl(slip)) {
      return false
    }

    // If not already added to wallet and slip consent is not needed
    return !addedToWallet.includes(slip.id) && this.needSlipConsent(slip) === false
  }

  needSlipConsent = document => {
    const { hasSlipConsent, countryIsoCode } = this.props

    if (hasSlipConsent === true) {
      return false
    }

    if (document && !(document.type === 'pinkSlips' || document.type === 'fleetSlips')) {
      return false
    }

    return countryIsoCode && countryIsoCode.toLowerCase() === 'ca'
  }

  getDocumentActions = () => {
    let response = {
      documentActions: [],
      highlightedActions: [],
      isFullWidth: true,
    }

    try {
      const { intl, document } = this.props
      const me = this

      if (isEmpty(document.links)) {
        return response
      }

      // Add vehicle related actions
      // This is a not a permanent solution.
      // TODO: make it configurable from pathway kiosk settings page
      if (
        (document.type === 'pinkSlips' || document.type === 'auto') &&
        !EXCLUDED_FOR_BROKERS.includes(this.props.subdomain)
      ) {
        response.documentActions = [
          {
            name: intl.formatMessage(intlMessages.documentLinkRemoveVehicle),
            onClick: () => me.redirect('/documents/vehicle/remove'),
          },
          {
            name: intl.formatMessage(intlMessages.documentLinkAddVehicle),
            onClick: () => me.redirect('/documents/vehicle/add'),
          },
          {
            name: intl.formatMessage(intlMessages.documentLinkRemoveDriver),
            onClick: () => me.redirect('/documents/driver/remove'),
          },
          {
            name: intl.formatMessage(intlMessages.documentLinkAddDriver),
            onClick: () => me.redirect('/documents/driver/add'),
          },
        ]
      }

      // Add Share action
      const shareUrl = this.getLinkUrl('documents.share')
      if (shareUrl) {
        response.documentActions.push({
          name: intl.formatMessage(intlMessages.documentLinkShare),
          onClick: () => this.openNewTab(`mailto:?body=${shareUrl}`),
        })
      }

      // Add highlighted "Add to wallet" action for slips and fleet slips
      if (document.type === 'pinkSlips' || document.type === 'fleetSlips') {
        const walletUrl = this.getLinkUrl('documents.wallet.download')
        // const isWalletVisible = this.isWalletButtonVisible(document)
        if (walletUrl) {
          // response.highlightedActions.push({
          //   isCenter: true,
          //   icon: isWalletVisible ? ['far', 'wallet'] : ['far', 'check'],
          //   name: isWalletVisible ? intl.formatMessage(intlMessages.documentLinkAddToWallet) : 'Added to Wallet',
          //   onClick: isWalletVisible ? () => this.addToWallet(walletUrl, document.id) : () => {}
          // })
          response.documentActions.push({
            isCenter: true,
            icon: ['far', 'wallet'],
            name: intl.formatMessage(intlMessages.documentLinkAddToWallet),
            onClick: () => this.addToWallet(walletUrl, document.id),
          })
        }
      }
      return response
    } catch (e) {
      return response
    }
  }

  render() {
    const { url, hasPreview, fetchingDownload, loading } = this.state
    const { intl, isWidget, features, document, countryIsoCode } = this.props

    const { documentActions, highlightedActions, isFullWidth } = this.getDocumentActions()

    const foundedFeature = features.find(f => f.id == 'documents')
    const title =
      foundedFeature && foundedFeature.title
        ? foundedFeature.title
        : intl.formatMessage(intlMessages.documentsHeadline)

    const documentTitle =
      (document && document.type == 'pinkSlips') || (document && document.type == 'fleetSlips')

    const isCanada = countryIsoCode && countryIsoCode.toLowerCase() === 'ca'

    return (
      <Layout className='m-documents-document-view'>
        <Content>
          <PageTitle
            title={
              documentTitle
                ? isCanada
                  ? intl.formatMessage(intlMessages.documentsPinkSlipHeadline)
                  : intl.formatMessage(intlMessages.documentsEIDHeadline)
                : intl.formatMessage(intlMessages.documentHeadline)
            }
            subtitle={title}
          />
          <Container>
            {loading ? (
              <div>
                <Loader styleVariation='staticWithHeight' />
              </div>
            ) : isEmpty(url) && hasPreview === false ? (
              <div className={styles.noPreviewWrapper}>
                <SvgIcon type='NoPreviewDoc' className={styles.noPreviewIcon} />
                <div className={styles.noPreviewTitle}>
                  {intl.formatMessage(intlMessages.documentsNoPreviewTitle)}
                </div>
                <div className={styles.noPreviewSubtitle}>
                  {intl.formatMessage(intlMessages.documentsNoPreviewSubtitle)}
                </div>
              </div>
            ) : (
              <div className={styles.imageWrapper}>
                <img src={url} alt={`${title} document`} />
              </div>
            )}
            <LockModal
              show={this.state.showLockModal}
              onClose={this.handleLockModal}
              language={intl.locale || ''}
            />
            <Button display-if={!isWidget} className={styles.fab} onClick={this.handleLockModal}>
              <FontAwesomeIconComponent icon={['far', 'lock-keyhole']} size='xs' type='text' />
            </Button>

            {!fetchingDownload && (
              <SpeedDial
                actions={documentActions}
                isFullWidth={isFullWidth}
                highlightedActions={highlightedActions}
                downloadDoc={this.handleDownloadDocument}
              />
            )}
          </Container>
        </Content>
      </Layout>
    )
  }
}

const DocumentViewContext = props => (
  <WidgetContext.Consumer>
    {context => <DocumentView {...props} {...context} />}
  </WidgetContext.Consumer>
)

export default withRouter(injectIntl(injectLanguage(DocumentViewContext)))
